import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { signIn as authSignIn, signOut as authSignOut, signUp as authSignUp, resetPassword as authResetPassword, updateUserMetadata as authUpdateUserMetadata } from '../services/authService';
import { getCurrentUserProfile } from '../services/userService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session from local storage (no network call)
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        getCurrentUserProfile()?.then(setUserProfile)?.catch(() => setUserProfile(null));
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        getCurrentUserProfile()?.then(setUserProfile)?.catch(() => setUserProfile(null));
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    const data = await authSignIn(email, password);
    return data;
  };

  const signUp = async (email, password, fullName, role) => {
    const data = await authSignUp(email, password, fullName, role);
    return data;
  };

  const signOut = async () => {
    await authSignOut();
    setUser(null);
    setUserProfile(null);
  };

  const resetPassword = async (email) => {
    await authResetPassword(email);
  };

  const updateUserMetadata = async (updates) => {
    const data = await authUpdateUserMetadata(updates);
    return data;
  };

  const refreshProfile = async () => {
    if (user) {
      const profile = await getCurrentUserProfile();
      setUserProfile(profile);
      return profile;
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateUserMetadata,
    refreshProfile,
    isAdmin: userProfile?.role === 'admin',
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
